import React, { useState } from 'react';
import axios from 'axios';
import { Leaf, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STANDARD_SUGGESTIONS = [
  'ASC', 'MSC', 'HACCP', 'EU Food Hygiene', 'BRCGS', 'GlobalG.A.P. Aquaculture',
];
const STATUS_OPTIONS = [
  { value: 'compliant',       label: 'Compliant' },
  { value: 'pending',         label: 'Pending' },
  { value: 'requires_review', label: 'Requires review' },
];

/**
 * BatchRecordingPanel — Sustainability (CO₂) + Compliance Standard logging
 * surfaces grouped in one card. Operator-facing, no mock data.
 */
const BatchRecordingPanel = ({ batchId, onLogged }) => {
  return (
    <section
      data-testid="batch-recording-panel"
      className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4"
    >
      <LogSustainabilityCard batchId={batchId} onLogged={onLogged} />
      <LogComplianceCard batchId={batchId} onLogged={onLogged} />
    </section>
  );
};

const authHeaders = () => {
  const token = localStorage.getItem('mvp_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const LogSustainabilityCard = ({ batchId, onLogged }) => {
  const [co2, setCo2] = useState('');
  const [location, setLocation] = useState('');
  const [event, setEvent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const v = parseFloat(co2);
    if (!Number.isFinite(v) || v <= 0) {
      toast.error('Enter a positive kg CO₂ value');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/batches/${batchId}/environmental-metrics`,
        {
          co2_emissions_kg: v,
          location: location.trim() || null,
          event_type: event.trim() || null,
        },
        { headers: authHeaders() },
      );
      toast.success(`${v} kg CO₂ logged`);
      setCo2(''); setLocation(''); setEvent('');
      if (typeof onLogged === 'function') await onLogged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to log CO₂');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="log-sustainability-card"
      className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6"
    >
      <div className="flex items-center gap-2 mb-3 sm:mb-4">
        <Leaf className="w-5 h-5 text-emerald-700" />
        <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
          Log Sustainability Metric
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FieldNumber label="CO₂ (kg) *" value={co2} onChange={setCo2} testid="ls-co2" placeholder="e.g. 4.2" />
        <FieldText label="Event (optional)" value={event} onChange={setEvent} testid="ls-event" placeholder="e.g. transport leg" />
        <FieldText label="Location (optional)" value={location} onChange={setLocation} testid="ls-location" placeholder="e.g. Oslo route" className="sm:col-span-2" />
      </div>
      <div className="mt-3 flex justify-end">
        <Button
          type="submit"
          disabled={submitting}
          data-testid="ls-submit"
          className="h-10 bg-emerald-700 hover:bg-emerald-800 text-white"
        >
          {submitting ? 'Logging…' : 'Log CO₂'}
        </Button>
      </div>
    </form>
  );
};

const LogComplianceCard = ({ batchId, onLogged }) => {
  const [standard, setStandard] = useState('');
  const [status, setStatus] = useState('compliant');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const std = standard.trim();
    if (!std) { toast.error('Standard is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/batches/${batchId}/compliance`,
        { standard: std, status, description: description.trim() || null },
        { headers: authHeaders() },
      );
      toast.success(`${std} logged`);
      setStandard(''); setDescription(''); setStatus('compliant');
      if (typeof onLogged === 'function') await onLogged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to log standard');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="log-compliance-card"
      className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6"
    >
      <div className="flex items-center gap-2 mb-3 sm:mb-4">
        <Shield className="w-5 h-5 text-emerald-700" />
        <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
          Log Compliance Standard
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FieldDatalist
          label="Standard *"
          value={standard}
          onChange={setStandard}
          testid="lc-standard"
          placeholder="e.g. ASC"
          options={STANDARD_SUGGESTIONS}
          listId="compliance-standard-suggestions"
        />
        <FieldSelect label="Status" value={status} onChange={setStatus} testid="lc-status" options={STATUS_OPTIONS} />
        <FieldText label="Description (optional)" value={description} onChange={setDescription} testid="lc-description" placeholder="e.g. Renewed Feb 2026" className="sm:col-span-2" />
      </div>
      <div className="mt-3 flex justify-end">
        <Button
          type="submit"
          disabled={submitting}
          data-testid="lc-submit"
          className="h-10 bg-emerald-700 hover:bg-emerald-800 text-white"
        >
          {submitting ? 'Logging…' : 'Log Standard'}
        </Button>
      </div>
    </form>
  );
};

/* ---------- Field primitives ---------- */
const baseInput =
  'mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400';
const FieldText = ({ label, value, onChange, placeholder, testid, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
    <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className={baseInput} />
  </label>
);
const FieldNumber = ({ label, value, onChange, placeholder, testid, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
    <input type="number" min="0" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className={baseInput} />
  </label>
);
const FieldSelect = ({ label, value, onChange, testid, options }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={baseInput}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </label>
);
const FieldDatalist = ({ label, value, onChange, placeholder, testid, options, listId }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
    <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} list={listId} data-testid={testid} className={baseInput} />
    <datalist id={listId}>{options.map((s) => <option key={s} value={s} />)}</datalist>
  </label>
);

export default BatchRecordingPanel;
